/**
 * QL-505 follow-up — the per-member rows inside the Invite Client dialog for
 * a Group client without a representative: name, link status, and
 * generate/copy/send/revoke, one row per current member. QL-654 — the same rows
 * serve a client with 2+ representatives (`basis === "representatives"`): one
 * row per representative, headed "Representatives". Lives inside
 * `invite-client-modal.tsx`'s Step 2 in place of the group's own Email/SMS
 * tabs (moved out of the standalone `member-access/member-links-dialog.tsx`,
 * which this replaces).
 */

import { parseDbTimestamp } from "@repo/shared";
import type { AnsweringListBasis, FlowInstanceMemberAccess, Translated } from "@repo/shared/types";
import { AlertCircle, Copy, Link2, Mail, MessageSquare, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/hooks/use-confirm";
import { apiErrorCode, apiErrorMessage } from "@/lib/api/_client";
import {
  useIssueMemberLink,
  useMemberAccess,
  useRevokeMemberLink,
  useSendMemberInvite,
} from "@/lib/hooks/use-flow-instances/use-member-access";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./member-links-section.i18n";

type MemberLinksT = Translated<typeof T>;

type MemberLinksSectionProps = {
  instanceId: string;
  /** The group client's own id, so an inline member's hint can link to its Members tab. */
  clientId: string;
  /**
   * The group's own access link is generated and not revoked. Member links
   * only work while it is, so until then every row is shown as unavailable.
   */
  groupLinkLive: boolean;
  /** QL-654 — why several parties answer: the group's members, or 2+ representatives. */
  basis: AnsweringListBasis;
};

type MemberAccessRow = FlowInstanceMemberAccess;

function memberStatus(
  member: FlowInstanceMemberAccess,
  t: MemberLinksT,
  formatDateTime: (date: Date | null) => string,
  groupLinkLive: boolean,
): string {
  // A representative signs in as themself; only a group member needs a linked client.
  if (member.subjectKind === "group_member" && !member.linkedClientId) return t.statusNotLinked;
  if (!groupLinkLive) return t.statusUnavailable;
  if (member.lastLoginAt) {
    return t.statusLastSignedIn(formatDateTime(parseDbTimestamp(member.lastLoginAt)));
  }
  if (member.invitedAt && member.invitedVia) {
    return t.statusInvited(
      member.invitedVia === "email" ? t.channelEmail : t.channelSms,
      formatDateTime(parseDbTimestamp(member.invitedAt)),
    );
  }
  if (member.url) return t.statusLinkReady;
  return t.statusNoLink;
}

export function MemberLinksSection({
  instanceId,
  clientId,
  groupLinkLive,
  basis,
}: MemberLinksSectionProps) {
  const t = useT(T);
  const { formatDateTime } = useRegionalFormat();
  const { members, isLoading, error } = useMemberAccess(instanceId);
  const issueLink = useIssueMemberLink(instanceId);
  const revokeLink = useRevokeMemberLink(instanceId);
  const sendInvite = useSendMemberInvite(instanceId);
  const { confirm, ConfirmationDialog } = useConfirm();
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const setError = (subjectId: string, message: string | null) =>
    setRowError((prev) => {
      const next = { ...prev };
      if (message) next[subjectId] = message;
      else delete next[subjectId];
      return next;
    });

  /** The translated hint for a known error code, else null. */
  const knownErrorHint = (err: unknown, name: string): string | null => {
    switch (apiErrorCode(err)) {
      case "member_not_linked":
        return t.notLinkedHint(name);
      case "public_access_not_enabled":
        return t.publicAccessNotEnabledHint;
      case "member_cannot_sign_in":
        return t.cannotSignIn(name);
      case "member_no_email":
        return t.noEmailHint;
      case "member_no_phone":
        return t.noPhoneHint;
      case "instance_not_editable":
        return t.instanceNotEditableHint;
      default:
        return null;
    }
  };

  const handleGenerate = async (subjectId: string, name: string) => {
    setError(subjectId, null);
    try {
      await issueLink.mutateAsync(subjectId);
    } catch (err) {
      setError(subjectId, knownErrorHint(err, name) ?? apiErrorMessage(err, t.genericError));
    }
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success(t.linkCopied);
  };

  const handleSend = async (subjectId: string, name: string, channel: "email" | "sms") => {
    setError(subjectId, null);
    try {
      await sendInvite.mutateAsync({ subjectId, channel });
      toast.success(t.inviteSent);
    } catch (err) {
      // SMS and e-mail failures arrive already translated by the server.
      setError(subjectId, knownErrorHint(err, name) ?? apiErrorMessage(err, t.genericError));
    }
  };

  const handleRevoke = async (subjectId: string, name: string) => {
    const confirmed = await confirm({
      title: t.revokeTitle,
      description: t.revokeDescription(name),
      confirmText: t.revokeConfirm,
      cancelText: t.cancel,
      variant: "destructive",
    });
    if (!confirmed) return;
    try {
      await revokeLink.mutateAsync(subjectId);
    } catch (err) {
      setError(subjectId, knownErrorHint(err, name) ?? apiErrorMessage(err, t.genericError));
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h4 className="font-medium text-sm">
          {basis === "representatives" ? t.titleRepresentatives : t.title}
        </h4>
        <p className="text-xs text-muted-foreground">
          {basis === "representatives" ? t.descriptionRepresentatives : t.description}
        </p>
      </div>

      {!groupLinkLive && (
        <Alert variant="warning">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">{t.groupLinkNotLive}</AlertDescription>
        </Alert>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">…</p>}
      {!isLoading && error && (
        <Alert variant="destructive" role="alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t.loadFailed}</AlertDescription>
        </Alert>
      )}
      {!isLoading &&
        !error &&
        members.map((row) => {
          const member = row as MemberAccessRow;
          const hint = rowError[member.subjectId] ?? null;
          const isGenerating = issueLink.isPending && issueLink.variables === member.subjectId;
          const sendingChannel =
            sendInvite.isPending && sendInvite.variables?.subjectId === member.subjectId
              ? sendInvite.variables.channel
              : null;
          const isRevoking = revokeLink.isPending && revokeLink.variables === member.subjectId;
          const rowBusy = isGenerating || sendingChannel !== null || isRevoking;
          const isRepresentative = member.subjectKind === "representative";
          // A representative signs in as themself; a group member through the linked client.
          const hasIdentity = isRepresentative || !!member.linkedClientId;
          const canGenerate = groupLinkLive && hasIdentity && member.canSignIn;
          const cannotSignIn = hasIdentity && member.canSignIn === false;
          const { representativeName } = member;
          return (
            <div key={member.subjectId} className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-medium text-sm">{member.name}</span>
                  <p className="text-xs text-muted-foreground">
                    {memberStatus(member, t, formatDateTime, groupLinkLive)}
                  </p>
                </div>
                {!member.url && canGenerate && (
                  <Button
                    size="sm"
                    onClick={() => handleGenerate(member.subjectId, member.name)}
                    disabled={rowBusy}
                    loading={isGenerating}
                  >
                    {t.generateLink}
                  </Button>
                )}
              </div>

              {!isRepresentative && !member.linkedClientId && (
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>{t.notLinkedHint(member.name)}</span>
                  <Button size="sm" variant="outline" asChild>
                    <Link to={`/clients/${clientId}?tab=members`}>
                      <Link2 className="mr-1.5 h-3.5 w-3.5" />
                      {t.linkToClient}
                    </Link>
                  </Button>
                </div>
              )}

              {representativeName && (
                <Alert variant="warning" className="py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    {t.representativeLink(representativeName, member.name)}
                  </AlertDescription>
                </Alert>
              )}

              {cannotSignIn ? (
                <Alert variant="destructive" className="py-2" role="alert">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    {isRepresentative ? (
                      <>
                        {t.cannotSignInRepresentative(member.name)}{" "}
                        <Link
                          to={`/clients/${clientId}?tab=${member.memberId ? "members" : "parties"}`}
                          className="underline"
                        >
                          {t.openRepresentatives}
                        </Link>
                      </>
                    ) : (
                      <>
                        {t.cannotSignIn(member.name)}{" "}
                        <Link to={`/clients/${member.linkedClientId}`} className="underline">
                          {t.openLinkedClient}
                        </Link>
                      </>
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                hasIdentity &&
                !member.hasSsn && <p className="text-xs text-muted-foreground">{t.noSsnNote}</p>
              )}

              {hint && (
                <Alert variant="destructive" className="py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">{hint}</AlertDescription>
                </Alert>
              )}

              {groupLinkLive && member.url && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1 px-2 py-1.5 bg-muted/50 rounded text-xs font-mono truncate">
                      {member.url}
                    </div>
                    <Button
                      size="icon"
                      variant="outline"
                      tooltip={t.copy}
                      onClick={() => handleCopy(member.url!)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      tooltip={t.revoke}
                      disabled={rowBusy}
                      loading={isRevoking}
                      onClick={() => handleRevoke(member.subjectId, member.name)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!member.email || rowBusy}
                      loading={sendingChannel === "email"}
                      tooltip={!member.email ? t.noEmailHint : undefined}
                      onClick={() => handleSend(member.subjectId, member.name, "email")}
                    >
                      <Mail className="mr-1.5 h-3.5 w-3.5" />
                      {t.sendEmail}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!member.phone || rowBusy}
                      loading={sendingChannel === "sms"}
                      tooltip={!member.phone ? t.noPhoneHint : undefined}
                      onClick={() => handleSend(member.subjectId, member.name, "sms")}
                    >
                      <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                      {t.sendSms}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      <ConfirmationDialog />
    </div>
  );
}
